import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { getQccReport } from "../services/apiClient.js";
import { formatDate } from "../utils/date.js";

// QCC Inspection Report — 1:1 port of QCCReport.aspx: inspected QCCs only,
// date range (defaults to last month), Passed/Failed filter, green Pass /
// red Fail result column. Rules doc: docs/business-rules/rdr-reports.md.
//
// Dressed as a DOCUMENT (report-document.css): an insurer asking how their jobs
// were checked gets this on GPS letterhead, not a screenshot of a screen.

// Show Columns (the control the feedback and File Life reports carry): thirteen
// columns do not fit any sheet of paper, and which ones matter depends on who the
// copy is for — an insurer wants their own jobs and the result, GPS wants the
// inspector's notes. Remembered per browser, everything on by default.
const COLUMN_DEFS = [
  ["date", "Date"], ["job", "Job #"], ["job_type", "Job Type"], ["pco", "PCO"],
  ["insurer", "Insurance Co"], ["assessor_co", "Assessor Co"], ["assessor", "Assessor"],
  ["claim", "Claim #"], ["client", "Client"], ["address", "Address"],
  ["result", "Result"], ["pm", "PM Checked"], ["notes", "Inspector Notes"],
];
const COLS_KEY = "qccCols:v1";

const RESULT_LABEL = { all: "Passed and failed", passed: "Passed only", failed: "Failed only" };

function QccReportPage() {
  const [report, setReport] = useState(null);
  const [f, setF] = useState({ from: "", to: "", result: "all" });
  // What the LOADED report covers, as opposed to what is typed in the filter bar.
  // The masthead has to describe the rows on the page, not a search not yet run.
  const [applied, setApplied] = useState("all");
  const [error, setError] = useState("");
  const loadCols = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_KEY));
      if (Array.isArray(saved)) return new Set(saved);
    } catch { /* fall through to all-on */ }
    return new Set(COLUMN_DEFS.map(([k]) => k));
  };
  const [cols, setCols] = useState(loadCols);
  const col = (k) => cols.has(k);
  const toggleCol = (k) => {
    setCols((c) => {
      const next = new Set(c);
      if (next.has(k)) next.delete(k); else next.add(k);
      localStorage.setItem(COLS_KEY, JSON.stringify([...next]));
      return next;
    });
  };
  const navigate = useNavigate();
  async function load(params = f) {
    setError("");
    try {
      const r = await getQccReport(params);
      setReport(r);
      setApplied(params.result || "all");
      setF((cur) => ({ ...cur, from: r.from, to: r.to }));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the QCC report.");
    }
  }

  useEffect(() => {
    load({ result: "all" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <Alert variant="danger">{error}</Alert>;

  const set = (name, value) => setF((cur) => ({ ...cur, [name]: value }));

  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="page-heading no-print">
        <h2>QCC Inspection Report <RulesHelp topic="qccReport" /></h2>
      </div>

      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <>
          <div className="report-controls d-flex flex-wrap align-items-end column-gap-2 row-gap-2 no-print">
            <div>
              <Form.Label className="mb-0 small text-muted">From</Form.Label>
              <Form.Control size="sm" type="date" value={f.from} onChange={(e) => set("from", e.target.value)} style={{ width: 160 }} />
            </div>
            <div>
              <Form.Label className="mb-0 small text-muted">To</Form.Label>
              <Form.Control size="sm" type="date" value={f.to} onChange={(e) => set("to", e.target.value)} style={{ width: 160 }} />
            </div>
            <div>
              <Form.Label className="mb-0 small text-muted">Result</Form.Label>
              <ThemedSelect size="sm" value={f.result} onChange={(e) => set("result", e.target.value)} style={{ width: 130 }}>
                <option value="all">All</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </ThemedSelect>
            </div>
            <Button size="sm" onClick={() => load()}>Search</Button>
            <span className="small text-muted ms-auto">
              {report.summary.n} inspection{report.summary.n === 1 ? "" : "s"}
              {report.summary.n > 0 ? (
                <>
                  {" · "}
                  <span className="text-success">{report.summary.passed} passed</span>
                  {" · "}
                  <span className="text-danger">{report.summary.failed} failed</span>
                  {" · "}{report.summary.pass_pct}% pass
                </>
              ) : null}
            </span>
          </div>

          <div className="d-flex align-items-center column-gap-3 flex-wrap my-2 no-print">
            <span className="small fw-semibold text-muted">Show columns:</span>
            {COLUMN_DEFS.map(([k, label]) => (
              <Form.Check
                key={k} inline type="checkbox" id={`qcc-col-${k}`} label={label}
                className="small" checked={cols.has(k)} onChange={() => toggleCol(k)}
              />
            ))}
          </div>

          <div className="report-doc report-doc-wide border rounded">
            <ReportMasthead
              report="QCC Inspection Report"
              title={RESULT_LABEL[applied] || RESULT_LABEL.all}
              subtitle={`Grayburn Property Services · ${formatDate(report.from)} to ${formatDate(report.to)}`}
            />
            <div className="report-body">
              {/* The pass rate belongs ON the document, not only in the filter bar:
                  it is the one figure a reader takes away, and it should survive
                  being printed and emailed. */}
              <div className="d-flex flex-wrap column-gap-4 pb-2 small text-muted">
                <span>{report.summary.n} inspection{report.summary.n === 1 ? "" : "s"}</span>
                <span>{report.summary.passed} passed</span>
                <span>{report.summary.failed} failed</span>
                <span className="fw-semibold">
                  {report.summary.n > 0 ? `${report.summary.pass_pct}% pass` : "No inspections to measure"}
                </span>
              </div>

              <div className="report-scroll">
                <Table striped bordered hover size="sm" className="mb-0 align-middle report-table" style={{ fontSize: "0.82rem" }}>
                  <thead className="table-surface">
                    <tr>
                      {col("date") ? <th>Date</th> : null}
                      {col("job") ? <th>Job #</th> : null}
                      {col("job_type") ? <th>Job Type</th> : null}
                      {col("pco") ? <th>PCO</th> : null}
                      {col("insurer") ? <th>Insurance Co</th> : null}
                      {col("assessor_co") ? <th>Assessor Co</th> : null}
                      {col("assessor") ? <th>Assessor</th> : null}
                      {col("claim") ? <th>Claim #</th> : null}
                      {col("client") ? <th>Client</th> : null}
                      {col("address") ? <th>Address</th> : null}
                      {col("result") ? <th className="text-center">Result</th> : null}
                      {col("pm") ? <th className="text-center">PM Checked</th> : null}
                      {col("notes") ? <th>Inspector Notes</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((r) => (
                      <tr key={r.id}>
                        {col("date") ? <td className="text-nowrap">{formatDate(r.inspection_date)}</td> : null}
                        {col("job") ? (
                          <td>
                            <span role="button" className="text-primary clickable" onClick={() => navigate(`/claims/${r.claim_number}`)}>
                              {r.claim_number}
                            </span>
                          </td>
                        ) : null}
                        {col("job_type") ? <td className="text-nowrap">{r.job_type_name || "-"}</td> : null}
                        {col("pco") ? <td className="text-nowrap">{r.pco_name || "-"}</td> : null}
                        {col("insurer") ? <td className="text-nowrap">{r.insurer_name || "-"}</td> : null}
                        {col("assessor_co") ? <td className="text-nowrap">{r.assessor_company_name || "-"}</td> : null}
                        {col("assessor") ? <td className="text-nowrap">{r.assessor_name || "-"}</td> : null}
                        {col("claim") ? <td className="text-nowrap">{r.insurance_claim_number || "-"}</td> : null}
                        {col("client") ? <td className="text-nowrap">{r.client_name || "-"}</td> : null}
                        {col("address") ? <td>{r.street_address || "-"}</td> : null}
                        {col("result") ? (
                          <td className="text-center">
                            {r.failed_qcc ? (
                              <span className="text-danger fw-semibold">Fail</span>
                            ) : (
                              <span className="text-success fw-semibold">Pass</span>
                            )}
                          </td>
                        ) : null}
                        {col("pm") ? <td className="text-center">{r.pm_checked ? "✓" : <span className="text-warning">—</span>}</td> : null}
                        {/* The notes are why the inspection is on the list, so they
                            wrap and claim the width rather than truncating. */}
                        {col("notes") ? <td className="report-col-main" style={{ whiteSpace: "pre-wrap", minWidth: 220 }}>{r.notes || "-"}</td> : null}
                      </tr>
                    ))}
                    {report.rows.length === 0 ? (
                      <tr><td colSpan={Math.max(1, cols.size)} className="text-center text-muted">No inspected QCCs in this range.</td></tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default QccReportPage;
